export default class PeopleClient {
    constructor(apiClient) {
        this.travelerUri = '/v3/travelers';
        this.travelerProfileUri = '/v3/traveler_profiles';
        this.agentUri = '/v3/agents';
        this.teamMemberUri = '/v3/team_members';
        this.apiClient = apiClient;
        this.getPerson = this.getPerson.bind(this);
        this.postTraveler = this.postTraveler.bind(this);
        this.putTraveler = this.putTraveler.bind(this);
        this.getTravelers = this.getTravelers.bind(this);
        this.travelerExists = this.travelerExists.bind(this);
        this.getTraveler = this.getTraveler.bind(this);
        this.getTravelerVersions = this.getTravelerVersions.bind(this);
        this.travelerVersionExists = this.travelerVersionExists.bind(this);
        this.getTravelerVersion = this.getTravelerVersion.bind(this);
        this.postTravelerDocument = this.postTravelerDocument.bind(this);
        this.getTravelerTripRequests = this.getTravelerTripRequests.bind(this);
        this.getTravelerConversations = this.getTravelerConversations.bind(this);
        this.getTravelerTripHistories = this.getTravelerTripHistories.bind(this);
        this.putTravelerProfile = this.putTravelerProfile.bind(this);
        this.getTravelerProfiles = this.getTravelerProfiles.bind(this);
        this.travelerProfileExists = this.travelerProfileExists.bind(this);
        this.getTravelerProfile = this.getTravelerProfile.bind(this);
        this.postAgent = this.postAgent.bind(this);
        this.putAgent = this.putAgent.bind(this);
        this.getAgents = this.getAgents.bind(this);
        this.agentExists = this.agentExists.bind(this);
        this.getAgent = this.getAgent.bind(this);
        this.getAgentVersions = this.getAgentVersions.bind(this);
        this.agentVersionExists = this.agentVersionExists.bind(this);
        this.getAgentVersion = this.getAgentVersion.bind(this);
        this.getAgentTripMatches = this.getAgentTripMatches.bind(this);
        this.postTeamMember = this.postTeamMember.bind(this);
        this.putTeamMember = this.putTeamMember.bind(this);
        this.getTeamMembers = this.getTeamMembers.bind(this);
        this.teamMemberExists = this.teamMemberExists.bind(this);
        this.getTeamMember = this.getTeamMember.bind(this);
        this.getTeamMemberVersions = this.getTeamMemberVersions.bind(this);
        this.teamMemberVersionExists = this.teamMemberVersionExists.bind(this);
        this.getTeamMemberVersion = this.getTeamMemberVersion.bind(this);
        this.getTravelerStatus = this.getTravelerStatus.bind(this);
        this.postTravelerSearch = this.postTravelerSearch.bind(this);
        this.postAgentSearch = this.postAgentSearch.bind(this);
        this.postTeamMemberSearch = this.postTeamMemberSearch.bind(this);
    }
    // This route only works with an email, not id
    getPerson(email, config) {
        return this.apiClient.get(`/v3/people/${email}`, config);
    }
    // Travelers
    postTraveler(data, config) {
        return this.apiClient.post(this.travelerUri, data, config);
    }
    putTraveler(id, data, config) {
        return this.apiClient.put(`${this.travelerUri}/${id}`, data, config);
    }
    getTravelers(config) {
        return this.apiClient.get(this.travelerUri, config);
    }
    travelerExists(idOrEmail, config) {
        return this.apiClient.exists(`${this.travelerUri}/${idOrEmail}`, config);
    }
    getTraveler(id, config) {
        return this.apiClient.get(`${this.travelerUri}/${id}`, config);
    }
    getTravelerVersions(id, config) {
        return this.apiClient.get(`${this.travelerUri}/${id}/versions`, config);
    }
    travelerVersionExists(id, updateId, config) {
        return this.apiClient.exists(`${this.travelerUri}/${id}/versions/${updateId}`, config);
    }
    getTravelerVersion(id, updateId, config) {
        return this.apiClient.get(`${this.travelerUri}/${id}/versions/${updateId}`, config);
    }
    getTravelerStatus(idOrEmail, config) {
        return this.apiClient.get(`${this.travelerUri}/${idOrEmail}/status`, config);
    }
    postTravelerDocument(id, data, config) {
        return this.apiClient.post(`${this.travelerUri}/${id}/documents`, data, config);
    }
    getTravelerTripRequests(id, config) {
        return this.apiClient.get(`${this.travelerUri}/${id}/trip_requests`, config);
    }
    getTravelerConversations(id, config) {
        return this.apiClient.get(`${this.travelerUri}/${id}/conversations`, config);
    }
    getTravelerTripHistories(id, config) {
        // Retrieves trips for the trips page based on the Traveler ID given.
        // Authorization: Traveler users
        // Returns:
        // - activeTrips: Trips that are currently active.
        // - PENDING and VERIFIED, createdAt <= 180 days ago
        // - MATCHED and REFERRED, trip request returnDate in the future
        // - SOLD, trip match returnDate in future
        // - Sort Order: SOLD, MATCHED, VERIFIED, PENDING
        // - pastTrips: Trips that have been completed.
        // - MATCHED and REFERRED, trip request returnDate in the past
        // - SOLD, trip match returnDate in past
        // - reviewPromptTrips: Trip IDs that need a review prompt.
        // - Trip Request status of SOLD
        // - Trip request visibilityFlags.hideReviewRequest is false
        // - Trip Match status of SOLD
        // - Trip Match returnDate is in the past but not more than 91 days ago
        // - There is no COMPLETED review for the trip match
        // - archivedTrips: Trips that are no longer active or relevant.
        // - VERIFIED and PENDING, createdAt > 180 days ago
        // - POSTPONED, JUNK and CANCELED
        return this.apiClient.get(`${this.travelerUri}/${id}/trip_histories`, config);
    }
    // Traveler Profiles
    putTravelerProfile(id, data, config) {
        return this.apiClient.put(`${this.travelerProfileUri}/${id}`, data, config);
    }
    getTravelerProfiles(config) {
        return this.apiClient.get(this.travelerProfileUri, config);
    }
    travelerProfileExists(id, config) {
        return this.apiClient.exists(`${this.travelerProfileUri}/${id}`, config);
    }
    getTravelerProfile(id, config) {
        return this.apiClient.get(`${this.travelerProfileUri}/${id}`, config);
    }
    // Agents
    postAgent(data, config) {
        return this.apiClient.post(this.agentUri, data, config);
    }
    putAgent(id, data, config) {
        return this.apiClient.put(`${this.agentUri}/${id}`, data, config);
    }
    getAgents(config) {
        return this.apiClient.get(this.agentUri, config);
    }
    agentExists(idOrEmail, config) {
        return this.apiClient.exists(`${this.agentUri}/${idOrEmail}`, config);
    }
    getAgent(id, config) {
        return this.apiClient.get(`${this.agentUri}/${id}`, config);
    }
    getAgentVersions(id, config) {
        return this.apiClient.get(`${this.agentUri}/${id}/versions`, config);
    }
    agentVersionExists(id, updateId, config) {
        return this.apiClient.exists(`${this.agentUri}/${id}/versions/${updateId}`, config);
    }
    getAgentVersion(id, updateId, config) {
        return this.apiClient.get(`${this.agentUri}/${id}/versions/${updateId}`, config);
    }
    getAgentTripMatches(id, config) {
        return this.apiClient.get(`${this.agentUri}/${id}/trip_matches`, config);
    }
    // Team Members
    postTeamMember(data, config) {
        return this.apiClient.post(this.teamMemberUri, data, config);
    }
    putTeamMember(id, data, config) {
        return this.apiClient.put(`${this.teamMemberUri}/${id}`, data, config);
    }
    getTeamMembers(config) {
        return this.apiClient.get(this.teamMemberUri, config);
    }
    teamMemberExists(idOrEmail, config) {
        return this.apiClient.exists(`${this.teamMemberUri}/${idOrEmail}`, config);
    }
    getTeamMember(id, config) {
        return this.apiClient.get(`${this.teamMemberUri}/${id}`, config);
    }
    getTeamMemberVersions(id, config) {
        return this.apiClient.get(`${this.teamMemberUri}/${id}/versions`, config);
    }
    teamMemberVersionExists(id, updateId, config) {
        return this.apiClient.exists(`${this.teamMemberUri}/${id}/versions/${updateId}`, config);
    }
    getTeamMemberVersion(id, updateId, config) {
        return this.apiClient.get(`${this.teamMemberUri}/${id}/versions/${updateId}`, config);
    }
    // Searches
    postTravelerSearch(data, config) {
        return this.apiClient.post('/v3/traveler_searches', data, config);
    }
    postAgentSearch(data, config) {
        return this.apiClient.post('/v3/agent_searches', data, config);
    }
    postTeamMemberSearch(data, config) {
        return this.apiClient.post('/v3/team_member_searches', data, config);
    }
}
